import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { getRfi } from '@Redux/actions/rfi';
import { AppDispatch } from '../../store.type';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import {
  CMS_JSON_DATA_VIEW_CONTENT_ID,
  DASHBOARD_URL,
  RFI_DETAILS,
  RFI_URL,
  RFIDATA
} from '@Utils/constants';
import { API_URL } from '@Utils/config';
import { getContentSchemaFromContentFul, makeApiCall } from '@Redux/actions/common';
import {
  isJumioServiceAvailable,
  jumioSuccessRequest
} from '@Redux/actions/individual-user-flow/individual-user-flow-action';
import { getCountryCode, getRfiCmsData } from '@Redux/selectors';
import { getEKYCRedirectUrl, setRfiCmsData } from '@Redux/actions/rfi/rfi-action';
import { Screens } from './utils';
import UnderReviewPage from './UnderReviewPage';
import EkycServiceDownPage from './EkycServiceDownPage';
import EkycRFISuccessPage from './EkycRFISuccessPage';
import IdentityCheck from './IdentityCheck';
import JumioRFIPage from './JumioRFIPage';
import { handleError, showMessage } from '@Redux/actions/common-message';
import LoadingPage from './LoadingPage';

const EkycLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState<string>(Screens.identityCheck);
  const [countryCodeFromJumio, setCountryCodeFromJumio] = useState<string>();
  const [rfiList, setRfiList] = useState<Record<string, any>[]>();
  const [iframeURL, setIframeURL] = useState<string | null>(null);
  const kycTypeRef = React.useRef();
  const [jumioStatus, setJumioStatus] = useState(null);
  const dispatch = useDispatch<AppDispatch>();
  const countryCode = useSelector(getCountryCode);
  const rfiCMSData = useSelector(getRfiCmsData)?.ekycRFI;

  useEffect(() => {
    fetchRfiList();

    return () => {
      // Clear message listener on component unmount
      window.removeEventListener('message', receiveMessage, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRFICmsData = async () => {
    const response = await getContentSchemaFromContentFul(
      CMS_JSON_DATA_VIEW_CONTENT_ID,
      RFI_DETAILS
    );
    dispatch(setRfiCmsData(response?.labels?.value || RFIDATA));
  };

  const fetchRFIData = async () => {
    const rfiResponse = await dispatch(getRfi());
    if (rfiResponse) {
      setRfiList(rfiResponse.data);
      const firstRfi = rfiResponse.data?.[0];
      /* If no rfi available, redirect user to Dashboard.
        If its not a KYC type RFI, redirect user to RFI_URL
      */
      if (rfiResponse.data?.length === 0) navigate(DASHBOARD_URL);
      else if (firstRfi?.document_code !== 'KYC_TYPE') navigate(RFI_URL);
      else resetStatesToOriginal();
    }
  };

  const fetchRfiList = async () => {
    dispatch(activateLoader());
    fetchRFIData();
    if (isEmpty(rfiCMSData)) {
      fetchRFICmsData();
    }
    dispatch(disableLoader());
  };

  const resetStatesToOriginal = () => {
    setCurrentScreen(Screens.identityCheck);
    setCountryCodeFromJumio('');
    setIframeURL(null);
    setJumioStatus(null);
  };

  const completeJumio = async (transactionReference: string, accountId: string) => {
    dispatch(activateLoader());
    const response = await dispatch(
      jumioSuccessRequest(
        true,
        rfiList?.[0]?.rfi_id || '',
        transactionReference,
        accountId,
        kycTypeRef.current
      )
    );
    if (response?.data?.data?.success) {
      dispatch(disableLoader());
      setCurrentScreen(Screens.loading);
    } else {
      dispatch(showMessage({ text: 'Error in submitting RFI', type: 'error' }));
      fetchRFIData();
    }
  };

  const receiveMessage = async (event: MessageEvent) => {
    try {
      if (event && event.data) {
        var data = window.JSON.parse(event.data);
        data && (await setJumioStatus(data.payload.value));
        if (data?.payload?.value === 'success') {
          setIframeURL(null);
          window.removeEventListener('message', receiveMessage, false);
          // redirect to next RFI screen
          completeJumio(data.workflowExecutionId, data.accountId);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);
    }
  };

  const initiateJumio = async (url: string) => {
    dispatch(activateLoader());
    setIframeURL(url);
    dispatch(disableLoader());
    window.addEventListener('message', receiveMessage, false);
    setCurrentScreen(Screens.jumio);
  };

  const fetchDropdownKYCTypeAndInitiateJumio = async () => {
    const firstRfi = rfiList?.[0];
    const url = firstRfi?.fields_json?.[0]?.url;
    const dropdownUrl = `${API_URL}${url}&country_code=${countryCode}`;
    try {
      const response = await makeApiCall({
        url: dropdownUrl,
        withCredentials: true
      });
      if (response?.data?.success && response.data.data) {
        const kycDropdownResponse = response.data.data[0];
        const firstRfiId = firstRfi?.rfi_id || '';
        // Used ref as in CompleteJumio accessing state variable have classic React state closure problem.
        kycTypeRef.current = kycDropdownResponse.value;
        const payload = { [firstRfiId]: { kyc_type: kycDropdownResponse.value } };
        const ekycResponse = await dispatch(getEKYCRedirectUrl(payload, true));
        if (ekycResponse?.data?.success) {
          const { redirect_url, country_code } = ekycResponse.data.data || {};
          if (redirect_url) {
            setCountryCodeFromJumio(country_code);
            initiateJumio(redirect_url);
          }
        }
      }
    } catch (err) {
      dispatch(handleError(err));
    }
  };

  const checkJumioAvailabilityAndInitiate = async () => {
    dispatch(activateLoader());
    const isJumioAvailable = await dispatch(isJumioServiceAvailable(countryCode, true));
    if (!isJumioAvailable) {
      dispatch(disableLoader());
      setCurrentScreen(Screens.ekycServiceDown);
    } else {
      fetchDropdownKYCTypeAndInitiateJumio();
    }
  };

  const changeScreen = (screenName: string) => {
    setCurrentScreen(screenName);
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case Screens.identityCheck:
        return (
          <IdentityCheck checkJumioAvailabilityAndInitiate={checkJumioAvailabilityAndInitiate} />
        );

      case Screens.jumio:
        return (
          <JumioRFIPage
            iframeURL={iframeURL}
            jumioStatus={jumioStatus}
            countryCodeFromJumio={countryCodeFromJumio}
            changeScreen={changeScreen}
          />
        );

      case Screens.loading:
        return <LoadingPage changeScreen={changeScreen} />;

      case Screens.ekycServiceDown:
        return <EkycServiceDownPage />;

      case Screens.success:
        return <EkycRFISuccessPage />;

      case Screens.pending:
        return <UnderReviewPage />;

      default:
        return <></>;
    }
  };
  return <>{renderScreen()}</>;
};

export default EkycLandingPage;
